import { MinusCircleFilled } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Accent, CustomButton, DocumentViewModal } from '@/lib/components';
import { Invoice } from '@/lib/types';
import {
  Badge,
  Col,
  Form,
  List,
  Popconfirm,
  Popover,
  Row,
  Select,
  Space,
  Alert,
} from 'antd';
import { getCheckedInvoiceSummary } from '../_helpers/inPaymentPageHelper';
import styles from '../styles.module.scss';
import {
  CheckedInvoiceSummary,
  PaymentConfirmSummary,
  PaymentSelectionSummary,
} from './InPaymentSummary';
import { t } from 'i18next';

type InPaymentToolbarProps = {
  bankAccounts: Array<{ id: number; iban: string }>;
  checkedList: number[];
  errorMessages: string[];
  hasAccess: { isAccountManager: boolean };
  isFrozen: boolean;
  noCheckedBoxes: boolean;
  sepaGenerateLoading: boolean;
  sepaInProgress: boolean;
  showErrorMessages: boolean;
  unusedCreditInvoices: Invoice[];
  onSubmit: () => void | Promise<void>;
  onRemoveCreditInvoice: (invoice: Invoice) => void | Promise<void>;
  onRemoveInvoiceBatch: () => void | Promise<void>;
  onRefreshPaymentsInProgress: () => void | Promise<void>;
  selectPlaceholder: string;
  paymentInProgressText: string;
  chooseBankAccountText: string;
  unusedCreditInvoiceText: string;
  setInPaymentBatchText: string;
  refreshText: string;
  sepaButtonText: string;
};

export const InPaymentToolbar = ({
  bankAccounts,
  checkedList,
  errorMessages,
  hasAccess,
  isFrozen,
  noCheckedBoxes,
  sepaGenerateLoading,
  sepaInProgress,
  showErrorMessages,
  unusedCreditInvoices,
  onSubmit,
  onRemoveCreditInvoice,
  onRemoveInvoiceBatch,
  onRefreshPaymentsInProgress,
  selectPlaceholder,
  paymentInProgressText,
  chooseBankAccountText,
  unusedCreditInvoiceText,
  setInPaymentBatchText,
  refreshText,
  sepaButtonText,
}: InPaymentToolbarProps) => {
  const form = Form.useFormInstance();

  return (
    <div
      style={{
        background: 'white',
        padding: '15px',
        borderRadius: '8px',
        boxShadow: '0px 4px 6px rgba(0,0,0,0.1)',
        marginLeft: '-10px',
        marginRight: '-10px',
      }}
    >
      <Row align="middle" gutter={16}>
        <Col flex={1}>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.invoices !== currentValues.invoices ||
              prevValues.iban !== currentValues.iban
            }
          >
            {() => {
              const summary: CheckedInvoiceSummary = getCheckedInvoiceSummary(
                form.getFieldValue('invoices'),
                checkedList,
              );

              return (
                <div
                  style={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 14,
                    paddingLeft: 2,
                  }}
                >
                  <PaymentSelectionSummary summary={summary} />
                  <Popconfirm
                    icon={null}
                    title={
                      <div style={{ width: '235px' }}>
                        <Form.Item
                          label={
                            <Accent color="tertiary">
                              {chooseBankAccountText}
                            </Accent>
                          }
                          name="iban"
                          initialValue={
                            bankAccounts.length === 1
                              ? bankAccounts[0].iban
                              : undefined
                          }
                          rules={[{ required: true }]}
                          style={{ marginTop: 8 }}
                          labelCol={{ span: 24 }}
                        >
                          <Select
                            style={{ width: '230px', fontSize: '1rem' }}
                            placeholder={selectPlaceholder}
                            bordered={false}
                            disabled={sepaInProgress}
                            options={bankAccounts?.map((bankAccount) => ({
                              label: bankAccount.iban,
                              value: bankAccount.iban,
                            }))}
                            suffixIcon={
                              <FontAwesomeIcon
                                icon={['fas', 'angle-down']}
                                size="lg"
                                className={styles.select}
                              />
                            }
                          />
                        </Form.Item>
                        <PaymentConfirmSummary summary={summary} />
                      </div>
                    }
                    cancelText={t('general.actions.cancel')}
                    onConfirm={onSubmit}
                    placement="bottomLeft"
                    okButtonProps={{ shape: 'round' }}
                    cancelButtonProps={{ shape: 'round' }}
                  >
                    <CustomButton
                      icon={
                        <FontAwesomeIcon
                          icon={['far', 'credit-card']}
                          size="sm"
                        />
                      }
                      shape="round"
                      type="primary"
                      color="secondary"
                      loading={sepaGenerateLoading}
                      toolTipKey="payment.action.createSepa"
                      disabled={
                        hasAccess.isAccountManager ||
                        isFrozen ||
                        sepaInProgress ||
                        noCheckedBoxes
                      }
                    >
                      {sepaButtonText}
                    </CustomButton>
                  </Popconfirm>
                </div>
              );
            }}
          </Form.Item>
        </Col>
        <Col flex="auto" style={{ marginRight: '20%' }}>
          <div>
            {errorMessages.length && showErrorMessages ? (
              <Alert
                description={
                  <ul>
                    {errorMessages.map((msg) => (
                      <li key={msg}>{msg}</li>
                    ))}
                  </ul>
                }
                type="error"
                showIcon
              />
            ) : null}
          </div>
        </Col>
        {unusedCreditInvoices.length > 0 && (
          <Col>
            <Popover
              title={unusedCreditInvoiceText}
              content={
                <List
                  dataSource={unusedCreditInvoices}
                  split={false}
                  bordered={false}
                  size="small"
                  renderItem={(invoice) => (
                    <List.Item>
                      {invoice.invoiceNumber}
                      <DocumentViewModal
                        id={invoice.sourceIdentifier}
                        idType="processfile"
                      />
                      <Badge
                        count={<MinusCircleFilled />}
                        offset={[-3, 3]}
                        style={{ color: '#f5222d', fontSize: '18px' }}
                      >
                        <CustomButton
                          shape="circle"
                          color="secondary"
                          ghost
                          toolTipKey="payment.action.deleteInvoice"
                          onClick={() => onRemoveCreditInvoice(invoice)}
                          icon={
                            <FontAwesomeIcon icon={['far', 'credit-card']} />
                          }
                        />
                      </Badge>
                    </List.Item>
                  )}
                />
              }
            >
              <Badge count={unusedCreditInvoices.length}>
                <CustomButton
                  color="primary"
                  shape="circle"
                  toolTipKey="payment.action.deleteInvoice"
                  icon={<FontAwesomeIcon icon="exclamation" />}
                />
              </Badge>
            </Popover>
          </Col>
        )}
        <Popconfirm
          title={setInPaymentBatchText}
          placement="topRight"
          cancelText={t('general.actions.cancel')}
          onConfirm={onRemoveInvoiceBatch}
          okButtonProps={{ shape: 'round' }}
          cancelButtonProps={{ shape: 'round' }}
        >
          <Badge
            count={<MinusCircleFilled />}
            offset={[-3, 3]}
            style={{ color: '#f5222d', fontSize: '18px' }}
          >
            <CustomButton
              ghost
              disabled={
                hasAccess.isAccountManager ||
                isFrozen ||
                sepaInProgress ||
                noCheckedBoxes
              }
              shape="circle"
              toolTipKey="payment.action.deleteInvoiceBatch"
              color="secondary"
              icon={<FontAwesomeIcon icon={['far', 'credit-card']} />}
            />
          </Badge>
        </Popconfirm>
      </Row>
      {sepaInProgress ? (
        <div style={{ paddingTop: '10px' }}>
          <Alert
            message={
              <Space size="small">
                <span>{paymentInProgressText}</span>
                <CustomButton
                  size="small"
                  type="primary"
                  ghost
                  onClick={onRefreshPaymentsInProgress}
                >
                  <FontAwesomeIcon icon="redo" rotation={270} />
                  {refreshText}
                </CustomButton>
              </Space>
            }
            type="info"
          />
        </div>
      ) : null}
    </div>
  );
};
